import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { MapPin, Radio, Loader2, RefreshCw, X, Terminal, Fingerprint, Wifi, Smartphone, Globe, Clock, Users } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Vinci Live Pulse · Admin heatmap panel.
 *
 * Renders:
 *   - Summary tiles (live now · hits 24h · sessions 24h · cities 24h · top city)
 *   - SVG India map with glowing pulse dots for each active session.
 *     Precise-fix sessions are shown in bright gold with an accuracy
 *     halo; IP-only sessions are dim bronze.
 *   - Top interested cities table filtered by category (steel / cement / all)
 *
 * India bounding box for the linear projection:
 *   longitude 68°E → 97°E · latitude 6°N → 37°N
 */
const IND_MIN_LNG = 68, IND_MAX_LNG = 97;
const IND_MIN_LAT = 6,  IND_MAX_LAT = 37;
const VB_W = 800, VB_H = 900;

function _project(lat, lng) {
  const x = ((lng - IND_MIN_LNG) / (IND_MAX_LNG - IND_MIN_LNG)) * VB_W;
  const y = VB_H - ((lat - IND_MIN_LAT) / (IND_MAX_LAT - IND_MIN_LAT)) * VB_H;
  return { x, y };
}

// A simplified India outline as an SVG path (approximate; good enough
// for the elite pulse aesthetic — not a survey-grade cartographic map).
const INDIA_PATH = "M 200,300 L 250,200 L 320,180 L 380,150 L 440,140 " +
  "L 500,180 L 560,220 L 610,270 L 660,320 L 700,380 L 720,450 L 710,520 " +
  "L 680,590 L 630,650 L 570,700 L 500,740 L 430,770 L 370,780 L 320,750 " +
  "L 280,690 L 240,620 L 210,540 L 200,460 L 210,380 Z";

export function AdminLivePulsePanel() {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState({ rows: [] });
  const [top, setTop] = useState({ rows: [] });
  const [category, setCategory] = useState("");
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(true);
  const [terminal, setTerminal] = useState([]);
  const [specs, setSpecs] = useState(null);        // for modal
  const [specsLoading, setSpecsLoading] = useState(false);
  const cursorRef = useRef("");

  const pollTerminal = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/admin/pulse/terminal?limit=25${cursorRef.current ? `&since=${encodeURIComponent(cursorRef.current)}` : ""}`);
      if (data.events?.length) {
        setTerminal((prev) => [...data.events, ...prev].slice(0, 60));
        cursorRef.current = data.cursor || cursorRef.current;
      } else if (!cursorRef.current && data.cursor) {
        cursorRef.current = data.cursor;
      }
    } catch { /* silent */ }
  }, [api]);

  const openSpecs = useCallback(async (fingerprint) => {
    if (!fingerprint) return;
    setSpecsLoading(true);
    setSpecs({ loading: true });
    try {
      const { data } = await api.get(`/admin/pulse/visitor/${fingerprint}`);
      setSpecs(data);
    } catch (e) {
      setSpecs({ error: e.response?.data?.detail || "Digital ID not found" });
    } finally { setSpecsLoading(false); }
  }, [api]);

  useEffect(() => {
    pollTerminal();
    const iv = setInterval(pollTerminal, 3500);
    return () => clearInterval(iv);
  }, [pollTerminal]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, t] = await Promise.all([
        api.get("/admin/pulse/summary"),
        api.get("/admin/pulse/live?minutes=30"),
        api.get(`/admin/pulse/top-cities?days=${days}${category ? "&category=" + category : ""}`),
      ]);
      setSummary(s.data);
      setLive(l.data);
      setTop(t.data);
    } catch (e) {
      /* silent */
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, category]);

  const nodes = useMemo(() => (live.rows || [])
    .filter(r => r.lat != null && r.lng != null)
    .map(r => ({ ...r, ...(_project(r.lat, r.lng)) })), [live]);

  const topCities = useMemo(() => {
    // Group top-cities by city (collapse categories)
    const grouped = {};
    for (const r of (top.rows || [])) {
      const key = r.city;
      if (!grouped[key]) grouped[key] = { ...r, hits: 0, unique_visitors: 0, cats: new Set() };
      grouped[key].hits += r.hits;
      grouped[key].unique_visitors += r.unique_visitors;
      if (r.category) grouped[key].cats.add(r.category);
    }
    return Object.values(grouped)
      .map(x => ({ ...x, categories: Array.from(x.cats).join(", ") }))
      .sort((a, b) => b.hits - a.hits).slice(0, 15);
  }, [top]);

  if (loading || !summary) {
    return <div className="p-10 text-center text-[#8A8A82]" data-testid="live-pulse-loading">
      <Loader2 className="animate-spin inline" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-live-pulse-panel">
      <div>
        <div className="overline text-[#B87333] inline-flex items-center gap-1.5">
          <Radio size={11} className="animate-pulse" /> Vinci Live Pulse · Visitor Intelligence
        </div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Live Pulse</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
          Real-time visitor map with two-layer geolocation — IP-level
          fallback (± 10-50 km) plus browser GPS for opt-in visitors
          (± 5-100 m). Every hit lands in the Market Intelligence
          ledger with brand + category context.
        </p>
      </div>

      {/* ── Summary tiles ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryTile label="Live now (30m)" value={summary.live_now}
                       accent="#2FB999" pulse dataTest="pulse-live-now" />
        <SummaryTile label="Hits · 24h" value={summary.hits_24h}
                       accent="#D8B23E" dataTest="pulse-hits-24h" />
        <SummaryTile label="Sessions · 24h" value={summary.sessions_24h}
                       accent="#B87333" dataTest="pulse-sessions-24h" />
        <SummaryTile label="Cities · 24h" value={summary.cities_24h}
                       accent="#F6E4A6" dataTest="pulse-cities-24h" />
        <SummaryTile label="Top city · 24h" value={summary.top_city_24h || "—"}
                       sub={summary.top_city_hits_24h
                              ? `${summary.top_city_hits_24h} hits` : ""}
                       accent="#D8B23E" dataTest="pulse-top-city" />
      </div>

      {/* ── Map + legend ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="border border-white/10 rounded-sm bg-[#050505] p-4"
              data-testid="pulse-map">
          <div className="flex items-center justify-between mb-2">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">
              India · active pulse ({nodes.length})
            </div>
            <button onClick={load} className="text-[#8A8A82] hover:text-[#D8B23E] p-1">
              <RefreshCw size={12} />
            </button>
          </div>
          <div className="relative rounded-sm overflow-hidden bg-gradient-to-br from-[#0A0906] via-[#050505] to-[#0F0C05]">
            <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-auto"
                  xmlns="http://www.w3.org/2000/svg">
              {/* backdrop */}
              <defs>
                <radialGradient id="pulseFill" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#0A0906" />
                  <stop offset="100%" stopColor="#030303" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <rect width={VB_W} height={VB_H} fill="url(#pulseFill)" />
              {/* India outline */}
              <path d={INDIA_PATH} fill="rgba(216,178,62,0.03)"
                    stroke="rgba(216,178,62,0.35)" strokeWidth={1.5} />

              {/* Grid: latitude / longitude marks */}
              {[10, 20, 30].map((lat) => {
                const y = _project(lat, IND_MIN_LNG).y;
                return <line key={`hlat${lat}`} x1={20} x2={VB_W - 20} y1={y} y2={y}
                              stroke="rgba(216,178,62,0.06)" strokeDasharray="2 4" />;
              })}
              {[75, 85].map((lng) => {
                const x = _project(IND_MIN_LAT, lng).x;
                return <line key={`vlng${lng}`} x1={x} x2={x} y1={20} y2={VB_H - 20}
                              stroke="rgba(216,178,62,0.06)" strokeDasharray="2 4" />;
              })}

              {/* Glowing pulse nodes */}
              {nodes.map((n) => {
                const isPrecise = !!n.precise;
                const colour = isPrecise ? "#F6E4A6" : "#B87333";
                const r = isPrecise ? 5 : 4;
                return (
                  <g key={n.session_id} data-testid={`pulse-node-${n.session_id}`}
                       filter="url(#glow)" style={{ cursor: "pointer" }}
                       onClick={() => openSpecs(n.fingerprint || n.session_id)}>
                    {/* Outer pulsing ring — CSS animation */}
                    <circle cx={n.x} cy={n.y} r={r * 2.5}
                              fill="none" stroke={colour} strokeOpacity={0.4}
                              strokeWidth={1}>
                      <animate attributeName="r" values={`${r};${r * 4};${r}`}
                                dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="stroke-opacity"
                                values="0.55;0;0.55" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={n.x} cy={n.y} r={r} fill={colour} />
                    <title>
                      {(n.city || "Unknown")} · {n.brand || "generic browse"} · {n.hits} hits{isPrecise ? " · precise GPS" : ""} · click for Technical Specs
                    </title>
                  </g>
                );
              })}
            </svg>
          </div>
          {/* Legend */}
          <div className="mt-3 flex items-center gap-4 text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82]">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F6E4A6]" />
              Precise GPS
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-[#B87333]" />
              IP-level
            </span>
          </div>
        </div>

        {/* ── Top cities ── */}
        <div className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a]"
              data-testid="pulse-top-cities">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">
                Top Interested Cities
              </div>
              <div className="text-[11px] text-[#8A8A82] font-body mt-0.5">
                Last {days} day{days > 1 ? "s" : ""}
                {category && ` · ${category}`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {["", "steel", "cement", "tmt"].map((c) => (
                <button key={c || "all"} data-testid={`pulse-cat-${c || "all"}`}
                        onClick={() => setCategory(c)}
                        className={`px-2 py-1 text-[9px] font-grotesk uppercase tracking-[0.18em] rounded-sm border ${
                          category === c ? "border-[#D8B23E] text-[#D8B23E]"
                                          : "border-white/10 text-[#8A8A82] hover:text-[#C9C9C2]"
                        }`}>
                  {c || "all"}
                </button>
              ))}
            </div>
          </div>
          {topCities.length === 0 ? (
            <div className="p-6 text-center text-[#6f6f68] italic font-body">
              No visitors yet — traffic will land here as soon as anyone browses.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="text-[#8A8A82]">
                <tr>
                  <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">City</th>
                  <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Brand</th>
                  <th className="text-right px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Hits</th>
                  <th className="text-right px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Users</th>
                </tr>
              </thead>
              <tbody>
                {topCities.map((r, i) => (
                  <tr key={r.city + i} data-testid={`pulse-city-${r.city}`}
                      className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-2 py-2">
                      <div className="text-[#F5F5F0] font-body">{r.city}</div>
                      <div className="text-[10px] text-[#6f6f68]">{r.region || "—"}</div>
                    </td>
                    <td className="px-2 py-2 text-[#D8B23E] font-body text-[11px]">{r.dominant_brand || "—"}</td>
                    <td className="px-2 py-2 text-right font-mono text-[#F5F5F0]">{r.hits}</td>
                    <td className="px-2 py-2 text-right font-mono text-[#2FB999]">{r.unique_visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 text-[10px] text-[#6f6f68] font-body italic border-t border-white/10 pt-2">
            Days window:
            {[1, 7, 30].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                      className={`ml-2 ${days === d ? "text-[#D8B23E]" : "text-[#8A8A82] hover:text-[#C9C9C2]"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Active Visitors · rich per-session detail ── */}
      <ActiveVisitorsTable rows={live.rows || []} onOpen={openSpecs} />

      {/* ── Terminal Log + Technical Specs ── */}
      <TerminalLog events={terminal} onOpen={openSpecs} />

      {specs && (
        <TechnicalSpecsModal specs={specs} loading={specsLoading}
                                onClose={() => setSpecs(null)} />
      )}
    </div>
  );
}

/* ── Active Visitors Table ─────────────────────────────── */
function _timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}

function _deviceIcon(t) {
  if (t === "Mobile") return "📱";
  if (t === "Tablet") return "📲";
  if (t === "Bot") return "🤖";
  return "🖥";
}

function ActiveVisitorsTable({ rows, onOpen }) {
  const sorted = [...(rows || [])].sort(
    (a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime()
  );
  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4"
          data-testid="pulse-active-visitors">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#2FB999] inline-flex items-center gap-2">
          <Users size={12} className="animate-pulse" />
          Active Visitors · Last 30 minutes ({sorted.length})
        </div>
        <div className="text-[10px] text-[#6f6f68] font-body">
          Click any row for full Technical Specs
        </div>
      </div>
      {sorted.length === 0 ? (
        <div className="p-6 text-center text-[#6f6f68] italic font-body">
          No live visitors — the tab you're on counts as one, so this fills up as soon as anyone else browses.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="pulse-active-visitors-table">
            <thead className="text-[#8A8A82]">
              <tr>
                <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">City & State</th>
                <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Device</th>
                <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Referrer</th>
                <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Interaction pulse</th>
                <th className="text-left px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">ISP</th>
                <th className="text-right px-2 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.session_id}
                    data-testid={`pulse-visitor-${r.session_id}`}
                    onClick={() => r.fingerprint && onOpen(r.fingerprint)}
                    className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-2 py-2">
                    <div className="text-[#F5F5F0] font-body inline-flex items-center gap-1.5">
                      <MapPin size={11} className={r.precise ? "text-[#F6E4A6]" : "text-[#B87333]"} />
                      {r.place || "Unknown"}
                    </div>
                    <div className="text-[10px] text-[#6f6f68]">
                      {r.country || "—"}{r.precise ? " · precise GPS" : ""}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-[#F5F5F0] font-body">
                      <span className="mr-1">{_deviceIcon(r.device_type)}</span>
                      {r.device_type || "Unknown"}
                    </div>
                    <div className="text-[10px] text-[#6f6f68]">
                      {r.connection_type || (r.mobile ? "Mobile Data" : "—")}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className={`font-body ${r.referrer_source === "Direct" ? "text-[#8A8A82]" : "text-[#D8B23E]"}`}>
                      {r.referrer_source || "Direct"}
                    </div>
                    {r.referrer && r.referrer_source !== "Direct" && r.referrer_source !== "Internal" && (
                      <div className="text-[10px] text-[#6f6f68] truncate max-w-[220px]" title={r.referrer}>
                        {(r.referrer || "").slice(0, 60)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-[#F5F5F0] font-body">{r.interaction || "Browsing"}</div>
                    <div className="text-[10px] text-[#6f6f68]">
                      {r.hits} hit{r.hits === 1 ? "" : "s"} · session {(r.session_id || "").slice(-6)}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-[#C9C9C2] font-body">
                    {r.isp || "—"}
                  </td>
                  <td className="px-2 py-2 text-right text-[10px] text-[#8A8A82] font-mono">
                    {_timeAgo(r.last_seen)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Scrolling Terminal Log ─────────────────────────────── */
function TerminalLog({ events, onOpen }) {
  return (
    <div data-testid="pulse-terminal-log"
           className="rounded-sm border border-[#2FB999]/30 bg-black p-3 max-h-64 overflow-y-auto font-mono text-[11px]"
           style={{ boxShadow: "0 0 40px -20px rgba(47,185,153,0.4)" }}>
      <div className="flex items-center justify-between mb-2 sticky top-0 bg-black py-1 border-b border-[#2FB999]/20">
        <div className="text-[#2FB999] flex items-center gap-2 uppercase tracking-[0.22em] text-[10px]">
          <Terminal size={11} className="animate-pulse" /> vinci.pulse ~ tail -f
        </div>
        <div className="text-[9px] text-[#5A5A55]">{events.length} events</div>
      </div>
      {events.length === 0 ? (
        <div className="text-[#5A5A55] italic">Awaiting first visitor ping…</div>
      ) : events.map((ev, i) => (
        <div key={`${ev.at}-${i}`}
               onClick={() => ev.digital_id && onOpen(ev.digital_id.replace("VDID-", "").toLowerCase().padEnd(64, "0"))}
               data-testid={`terminal-event-${i}`}
               className={`py-0.5 ${ev.digital_id ? "cursor-pointer hover:text-[#F6E4A6]" : ""}`}>
          <span className="text-[#5A5A55]">{(ev.at || "").slice(11, 19)}</span>{" "}
          <span className={ev.mobile ? "text-[#B87333]" : "text-[#2FB999]"}>
            {ev.mobile ? "▲" : "◆"}
          </span>{" "}
          <span className="text-[#2FB999]">{ev.line}</span>
          {ev.isp && <span className="text-[#5A5A55]"> · {ev.isp}</span>}
        </div>
      ))}
    </div>
  );
}

/* ── Technical Specs Modal ─────────────────────────────── */
function TechnicalSpecsModal({ specs, loading, onClose }) {
  return (
    <div data-testid="specs-modal"
           className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
           onClick={onClose}>
      <div className="bg-[#0A0906] border border-[#D8B23E]/50 rounded-sm max-w-3xl w-full max-h-[85vh] overflow-y-auto"
             onClick={(e) => e.stopPropagation()}
             style={{ boxShadow: "0 40px 120px -30px rgba(216,178,62,0.45)" }}>
        <div className="sticky top-0 bg-[#0A0906] border-b border-[#D8B23E]/30 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Fingerprint className="text-[#D8B23E]" size={22} />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Admin Radar · Technical Specs</div>
              <div className="font-serif text-lg text-[#F6E4A6]">
                {specs.digital_id || "Loading…"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20} /></button>
        </div>
        {loading || specs.loading ? (
          <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>
        ) : specs.error ? (
          <div className="p-6 text-[#C0392B] font-body">{specs.error}</div>
        ) : (
          <div className="p-6 space-y-5">
            <SpecsRow icon={<Wifi size={12} />} title="Network Intelligence" items={[
              ["ISP", specs.network?.isp],
              ["Org", specs.network?.org],
              ["ASN", specs.network?.asn],
              ["Connection", specs.network?.connection_type],
              ["Mobile", specs.network?.mobile ? "Yes" : "No"],
              ["Proxy / VPN", specs.network?.proxy ? "Yes" : "No"],
              ["Hosting / DC", specs.network?.hosting ? "Yes" : "No"],
            ]} />
            <SpecsRow icon={<Globe size={12} />} title="Deep Geolocation" items={[
              ["City", specs.geo?.city], ["District", specs.geo?.district],
              ["Region", specs.geo?.region], ["Zip", specs.geo?.zip],
              ["Country", specs.geo?.country], ["Timezone", specs.geo?.timezone],
            ]} />
            <SpecsRow icon={<Smartphone size={12} />} title="Device Fingerprint" items={[
              ["Screen", specs.device?.screen], ["Languages", specs.device?.languages],
              ["TZ offset (min)", specs.device?.tz_offset_min],
              ["Seen count", specs.seen_count],
              ["First seen", (specs.first_seen || "").slice(0, 19).replace("T", " ")],
              ["Last seen", (specs.last_seen || "").slice(0, 19).replace("T", " ")],
            ]} />
            <div className="text-[10px] text-[#5A5A55] font-mono break-all pt-3 border-t border-white/10">
              UA: {specs.device?.user_agent || "—"}
            </div>
            {specs.recent_trail?.length > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E] mb-2 flex items-center gap-1.5">
                  <Clock size={11} /> Session Replay · Recent Path
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto text-[11px] font-mono">
                  {specs.recent_trail.map((t, i) => (
                    <div key={i} className="text-[#C9C9C2] flex gap-3 py-0.5 border-b border-white/5">
                      <span className="text-[#5A5A55] w-40 shrink-0">{(t.occurred_at || "").slice(11, 19)}</span>
                      <span className="flex-1 truncate">{new URL(t.page_url || "http://x/").pathname}</span>
                      {t.brand && <span className="text-[#D8B23E]">{t.brand}</span>}
                      {t.time_on_prev_ms > 0 && <span className="text-[#2FB999]">{Math.round(t.time_on_prev_ms / 1000)}s</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SpecsRow({ icon, title, items }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E] mb-2 flex items-center gap-1.5">
        {icon} {title}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map(([k, v]) => (
          <div key={k}>
            <div className="text-[9px] tracking-[0.18em] uppercase text-[#8A6A1A]">{k}</div>
            <div className="text-[13px] text-[#F5F5F0] font-body break-words">{(v ?? "—").toString() || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, accent, pulse, dataTest }) {
  return (
    <div className="border border-white/10 rounded-sm p-3 bg-[#0F0C05]"
          data-testid={dataTest}
          style={{ boxShadow: `0 0 40px -20px ${accent}` }}>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
            style={{ color: accent }}>
        {pulse && <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ background: accent }} />}
        {label}
      </div>
      <div className="font-serif text-2xl mt-1 text-[#F5F5F0] tabular-nums truncate">{value}</div>
      {sub && <div className="text-[10px] text-[#8A8A82] font-body">{sub}</div>}
    </div>
  );
}


/**
 * Compact tile version for the Admin dashboard overview.
 */
export function AdminLivePulseTile() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    let m = true;
    const load = async () => {
      try { const { data } = await api.get("/admin/pulse/summary"); if (m) setD(data); }
      catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { m = false; clearInterval(iv); };
  }, [api]);

  const live = d?.live_now ?? 0;
  const active = live > 0;
  const accent = active ? "#2FB999" : "#8A8A82";

  return (
    <button type="button"
             onClick={() => (window.location.href = "/admin?tab=live-pulse")}
             data-testid="admin-live-pulse-tile"
             className={`w-full text-left rounded-sm border p-6 md:p-7 mb-4 transition-colors flex items-center justify-between gap-6
                          ${active ? "border-[#2FB999]/50 bg-[#02150F] hover:border-[#2FB999]"
                                    : "border-white/10 bg-black/40 hover:border-[#C9A961]/40"}`}>
      <div>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2"
              style={{ color: accent }}>
          <Radio size={11} className={active ? "animate-pulse" : ""} /> Vinci Live Pulse
        </div>
        <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">
          {d
            ? (active
                ? <>{live} visitor{live === 1 ? "" : "s"} browsing right now</>
                : "No live visitors in the last 30m")
            : "Loading pulse…"}
        </div>
        {d && (
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#C9C9C2] font-body">
            <span><b className="text-[#F5F5F0]">{d.hits_24h}</b> hits · 24h</span>
            <span><b className="text-[#F5F5F0]">{d.sessions_24h}</b> sessions</span>
            <span><b className="text-[#F5F5F0]">{d.cities_24h}</b> cities</span>
            {d.top_city_24h && (
              <span className="text-[#D8B23E]">Top · {d.top_city_24h} ({d.top_city_hits_24h})</span>
            )}
          </div>
        )}
      </div>
      <div className="hidden md:flex flex-col items-end gap-2">
        <MapPin size={22} style={{ color: accent }} className={active ? "animate-pulse" : ""} />
        <span className="font-grotesk text-[10px] uppercase tracking-[0.28em]"
              style={{ color: accent }}>Open map →</span>
      </div>
    </button>
  );
}
